import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip, ButtonMenu } from '@ann/web-components';
import { WIDGET_GRID_COPY } from '../../constants';
import { useWidgetGridContext } from '../../context';
import {
  Shell,
  Header,
  HeaderLeft,
  Title,
  Description,
  HeaderActions,
  DragHandleWrapper,
  BodyTopSlot,
  ContentArea,
  FooterSlot,
} from './WidgetCardShell.styled';

const MAX_VISIBLE_ACTIONS = 2;

/**
 * Widget shell component driven by the backend contract.
 *
 * Header: title, optional description, action buttons from contract.
 * Shows first 2 buttons directly, overflows the rest into a ButtonMenu.
 * Drag handle: visible on hover when isEditMode.
 *
 * @param {Array} [props.headerButtons] - Action buttons from config.card.header_buttons.
 *   Each item: { key, icon, tooltip, action: { type, target, ... } }
 * @param {Function} [props.onAction] - Callback when a header button is clicked.
 *   Receives the action object: onAction(button.action)
 */
const WidgetCardShell = ({
  title,
  description,
  headerButtons,
  onAction,
  onDelete,
  onPin,
  isPinned,
  bodyTopSlot,
  footerSlot,
  footerDisposition,
  children,
}) => {
  const { isEditMode } = useWidgetGridContext();
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const buttons = useMemo(() => headerButtons || [], [headerButtons]);
  const visibleButtons = useMemo(() => buttons.slice(0, MAX_VISIBLE_ACTIONS), [buttons]);
  const overflowButtons = useMemo(() => buttons.slice(MAX_VISIBLE_ACTIONS), [buttons]);

  const showHeader = isEditMode || title || description || buttons.length > 0;

  const handleOverflowSelect = useCallback(
    (value) => {
      const btn = overflowButtons.find((b) => b.key === value);
      if (btn?.action) onAction?.(btn.action);
    },
    [overflowButtons, onAction],
  );

  return (
    <Shell
      $isEditMode={isEditMode}
      $isHovered={isHovered}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showHeader && (
        <Header>
          {isEditMode && (
            <DragHandleWrapper $visible={isHovered}>
              <Button
                type="tertiary"
                size="small"
                iconLeft="dragIndicator"
                className="widget-drag-handle"
                aria-label={WIDGET_GRID_COPY.actions.dragWidget}
              />
            </DragHandleWrapper>
          )}
          <HeaderLeft>
            {title && <Title>{title}</Title>}
            {description && <Description>{description}</Description>}
          </HeaderLeft>
          <HeaderActions>
            {visibleButtons.map((btn) => (
              <Tooltip key={btn.key} content={btn.tooltip} position="bottom" arrow={false}>
                <Button
                  type="tertiary"
                  size="small"
                  iconLeft={btn.icon}
                  aria-label={btn.tooltip}
                  onClick={() => onAction?.(btn.action)}
                />
              </Tooltip>
            ))}
            {overflowButtons.length > 0 && (
              <ButtonMenu
                iconPosition="left"
                label=""
                menuPosition="bottom"
                onChange={handleOverflowSelect}
                size="small"
                type="tertiary"
              >
                <ButtonMenu.Menu>
                  {overflowButtons.map((btn) => (
                    <ButtonMenu.Item key={btn.key} label={btn.tooltip} value={btn.key}>
                      {btn.tooltip}
                    </ButtonMenu.Item>
                  ))}
                </ButtonMenu.Menu>
              </ButtonMenu>
            )}
            {isEditMode && onPin && (
              <Tooltip content={isPinned ? WIDGET_GRID_COPY.actions.unpinWidget : WIDGET_GRID_COPY.actions.pinWidget} position="bottom" arrow={false}>
                <Button
                  type="tertiary"
                  size="small"
                  iconLeft={isPinned ? 'lock' : 'lock_open'}
                  aria-label={isPinned ? WIDGET_GRID_COPY.actions.unpinWidget : WIDGET_GRID_COPY.actions.pinWidget}
                  onClick={onPin}
                />
              </Tooltip>
            )}
            {isEditMode && onDelete && (
              <Tooltip content={WIDGET_GRID_COPY.actions.removeWidget} position="bottom" arrow={false}>
                <Button
                  type="tertiary"
                  size="small"
                  iconLeft="delete"
                  aria-label={WIDGET_GRID_COPY.actions.removeWidget}
                  onClick={onDelete}
                />
              </Tooltip>
            )}
          </HeaderActions>
        </Header>
      )}
      {bodyTopSlot && <BodyTopSlot>{bodyTopSlot}</BodyTopSlot>}
      <ContentArea>{children}</ContentArea>
      {footerSlot && (
        <FooterSlot $disposition={footerDisposition}>{footerSlot}</FooterSlot>
      )}
    </Shell>
  );
};

WidgetCardShell.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  headerButtons: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.string,
      tooltip: PropTypes.string,
      action: PropTypes.object,
    }),
  ),
  onAction: PropTypes.func,
  onDelete: PropTypes.func,
  onPin: PropTypes.func,
  isPinned: PropTypes.bool,
  bodyTopSlot: PropTypes.node,
  footerSlot: PropTypes.node,
  footerDisposition: PropTypes.oneOf(['horizontal', 'vertical']),
  children: PropTypes.node,
};

WidgetCardShell.defaultProps = {
  title: '',
  description: '',
  headerButtons: null,
  onAction: undefined,
  onDelete: undefined,
  onPin: undefined,
  isPinned: false,
  bodyTopSlot: null,
  footerSlot: null,
  footerDisposition: 'horizontal',
  children: null,
};

export default WidgetCardShell;
